import React, { useEffect } from 'react';
import { Layer, Image, Transformer, Text, Circle, Line, Rect, Group } from "react-konva";
import NormalColumn from './NormalColumns/normalColumn';
import MastColumnAsym from './MastColumns/mastColumnAsym';
import MastColumnBeam from './MastColumns/mastColumnBeam';
import MastColumnNone from './MastColumns/mastColumnNone';
import WallColumnAsym from './MastWalls/mastWallAsym';
import WallColumnBeam from './MastWalls/mastWallBeam';
import WallColumnNone from './MastWalls/mastWallNone';

export default function ColumnHandler(props) {
    const { lanterns, isMast = false, mountingType, colorList, luminaires, dxfScale } = props;
    const trRef = React.useRef();
    const textRef = React.useRef();
    const shapeRef = React.useRef();
    const lineRefs = React.useRef([]);
    const [position, setPosition] = React.useState({ x: 0, y: 0 });
    const [hoverColumn, setHoverColumn] = React.useState(false);
    const [textWidth, setTextWidth] = React.useState(0);

    useEffect(() => {
        setPosition({ x: props.values.point.x, y: props.values.point.y });
    }, [props.values.point.x, props.values.point.y]);

    const luminaireSignature = React.useMemo(() => {
        const ids = (props.values?.lanterns || [])
            .map((lantern) => lantern?.luminaireId)
            .filter(Boolean);
        // Only luminaire IDs matter for polar lookup; beam drag updates should not retrigger.
        return Array.from(new Set(ids)).sort().join("|");
    }, [props.values?.lanterns]);

    useEffect(() => {
        props.fetchPolarInfo(props.values.lanterns);
    }, [luminaireSignature]);

    const syncTransformer = () => {
        if (!trRef.current || !shapeRef.current) return;

        if (props.selected) {
            trRef.current.nodes([shapeRef.current]);
            trRef.current.position(shapeRef.current.position());
            trRef.current.rotation(renderAngle);
            trRef.current.getLayer()?.batchDraw();
            trRef.current.update();
        } else {
            trRef.current.nodes([]);
            trRef.current.getLayer()?.batchDraw();
        }
    };

    useEffect(() => {
        syncTransformer();
    }, [props.selected, props.positionAngle, props.flipY, props.worldHeight, mountingType, isMast]);

    const getDynamicFontSize = () => {
        return Math.max(.2, 10 / props.stageScale);
    };

    useEffect(() => {
        if (textRef.current) {
            const box = textRef.current.getClientRect();
            setTextWidth(box.width / props.stageScale);
        }
    }, [props.name, props.stageScale, hoverColumn]);

    const shouldFlipY = Boolean(props.flipY && Number.isFinite(props.worldHeight));
    const stageYUp = Boolean(props.stageYUp);
    const toWorldY = (y) => (shouldFlipY ? (props.worldHeight - y) : y);
    const renderPosition = shouldFlipY
        ? { x: position.x, y: props.worldHeight - position.y }
        : position;
    const normalize360 = (angle) => ((angle % 360) + 360) % 360;
    // Konva transformer angle behaves opposite to our stored clockwise UI angle.
    const renderAngle = normalize360(props.positionAngle || 0);
    // console.log("Rendering column", props.name, props.positionAngle, "calculated renderPosition", renderPosition, "renderAngle", renderAngle);
    const toStoredAngle = (angle) => {
        const normalized = normalize360(angle);
        return normalize360(-normalized);
    };

    const handleDragMove = (e) => {
        const worldX = e.target.x() / dxfScale;
        const worldY = toWorldY(e.target.y() / dxfScale);
        setPosition({ x: worldX, y: worldY });
    };

    const dxf = dxfScale;

    // Define base min/max sizes
    const minDivisor = 24;

    // Helper to interpolate between min (/15) and max (normal) sizes
    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    // Use sf (0.1–1 range) to interpolate — clamp between 0–1 just in case
    const safeZoom = Math.max(0.1, Number(props.stageScale) || 1);

    // Compute dynamically scaled values
    const baseRadius = clamp(10 / safeZoom, 10 / minDivisor, 10);
    const baseArmLength = clamp(11 / safeZoom, 11 / minDivisor, 11);
    const handleBaseSize = clamp(2.8 / safeZoom, 2.8 / minDivisor, 2.8);
    const beamHandleBaseSize = clamp(4 / safeZoom, 4 / minDivisor, 4);

    // Then apply drawing scale and normalize by grid scale so symbols stay visually stable.
    const avgGridScale = Math.max(
        1,
        (Math.abs(Number(props.gridScaleX) || 1) + Math.abs(Number(props.gridScaleY) || 1)) / 2
    );
    const columnScale = (baseRadius * dxf) / avgGridScale;
    const circleRadius = Math.max(0.12, columnScale);
    // 0.5m diameter visual minimum (0.25m radius), shared baseline for mast/column.
    const mastCircleRadius = Math.max(0.25, circleRadius / 2);
    const mastCrossStrokeWidth = 0.05;
    // Keep the X fully inside the mast circle and slightly inset from the edge.
    const mastCrossInsetFactor = 0.85;
    const crossHalfExtent = Math.max(0, (mastCircleRadius - mastCrossStrokeWidth / 2) * mastCrossInsetFactor) / Math.sqrt(2);

    const fullArmLength = Math.max(0.3, baseArmLength * dxf / avgGridScale);
    const endPointSize = Math.max(0.1, handleBaseSize * dxf / avgGridScale);
    const beamEndPointSize = Math.max(0.12, beamHandleBaseSize * dxf / avgGridScale);



    const textPosX = renderPosition.x - (textWidth / 2);
    // const textPosY = renderPosition.y - ((getDynamicFontSize() + 2.75) * 2) - 1;
    const textPosY = renderPosition.y + 8;
    const textFlipProps = stageYUp
        ? { scaleY: -1, offsetY: getDynamicFontSize() }
        : {};
    const anchorOffset = 100;
    const isLocked = Boolean(props.locked);
    const handleSelect = () => {
        if (!isLocked) {
            props.selectColumn(props.id);
        }
    };

    useEffect(() => {
        syncTransformer();
    }, [props.selected, props.id, renderAngle]);


    return (
        <>
            {!isMast && (
                <NormalColumn
                    {...props}
                    position={renderPosition}
                    groupRef={shapeRef}
                    handleDragMove={handleDragMove}
                    circleRadius={circleRadius}
                    getDynamicFontSize={getDynamicFontSize}
                    textPosX={textPosX}
                    textPosY={textPosY}
                    fullArmLength={fullArmLength}
                    endPointSize={endPointSize}
                    positionAngle={renderAngle}
                    stageYUp={stageYUp}
                />
            )}



            {isMast && mountingType === "column" && (
                <>
                    {props.values.configuration == "None" && (
                        <MastColumnNone
                            lanterns={lanterns}
                            position={renderPosition}
                            props={{ ...props, positionAngle: renderAngle }}
                            circleRadius={circleRadius}
                            colorList={colorList}
                            luminaires={luminaires}
                            fullArmLength={fullArmLength}
                            endPointSize={endPointSize}
                        />
                    )}

                    {(props.values.configuration == "Beam Aiming") && (
                        <MastColumnBeam
                            lanterns={lanterns}
                            position={renderPosition}
                            props={{ ...props, positionAngle: renderAngle }}
                            colorList={colorList}
                            luminaires={luminaires}
                            fullArmLength={fullArmLength}
                            endPointSize={beamEndPointSize}
                        />
                    )}

                    {(props.values.configuration == "Asymmetrical Flood Aiming") && (
                        <MastColumnAsym
                            lanterns={lanterns}
                            position={renderPosition}
                            props={{ ...props, positionAngle: renderAngle }}
                            colorList={colorList}
                            luminaires={luminaires}
                            fullArmLength={fullArmLength}
                            endPointSize={beamEndPointSize}
                        />
                    )}

                    {hoverColumn && (
                        <Text
                            ref={textRef}
                            x={textPosX}
                            y={textPosY}
                            text={props.name}
                            fontSize={getDynamicFontSize()}
                            fill="black"
                            fontStyle="bold"
                            padding={5}
                            backgroundColor="gray"
                            opacity={0.75}
                            listening={false}
                            {...textFlipProps}
                        />
                    )}

                    {/* Central mast circle */}
                    <Group>
                        <Circle
                            x={renderPosition.x}
                            y={renderPosition.y}
                            rotation={renderAngle}
                            radius={mastCircleRadius}
                            fill={'lightgray'}
                            hitStrokeWidth={Math.max(0.18, 6 / Math.max(0.1, Number(props.stageScale) || 1))}
                            draggable={props.selected && !isLocked}
                            ref={shapeRef}
                            onClick={handleSelect}
                            onTap={handleSelect}
                            onDragMove={handleDragMove}
                            onDragEnd={(e) => props.updateColumn(e, props.i)}
                            onMouseEnter={() => setHoverColumn(true)}
                            onMouseLeave={() => setHoverColumn(false)}
                        />

                        {/* cross lines */}
                        <Line
                            points={[
                                renderPosition.x - crossHalfExtent, renderPosition.y - crossHalfExtent,
                                renderPosition.x + crossHalfExtent, renderPosition.y + crossHalfExtent,
                            ]}
                            stroke="black"
                            strokeWidth={mastCrossStrokeWidth}
                            lineCap="round"
                            listening={false}
                            hitStrokeWidth={0}
                        />
                        <Line
                            points={[
                                renderPosition.x - crossHalfExtent, renderPosition.y + crossHalfExtent,
                                renderPosition.x + crossHalfExtent, renderPosition.y - crossHalfExtent,
                            ]}
                            stroke="black"
                            strokeWidth={mastCrossStrokeWidth}
                            lineCap="round"
                            listening={false}
                            hitStrokeWidth={0}
                        />
                    </Group>
                </>
            )}





            {isMast && mountingType === "wall" && (() => {
                const offsets = lanterns.map((l) => parseFloat(l.offset) || 0);
                const minOffset = Math.min(...offsets);
                const maxOffset = Math.max(...offsets);
                const centerOffset = (minOffset + maxOffset) / 2;
                const width = (Math.max(circleRadius, maxOffset - minOffset) + circleRadius);

                const angle = renderAngle;
                const barThickness = 0.4;

                const barCenter = {
                    x: renderPosition.x,
                    y: renderPosition.y,
                };


                function rotateAround(point, center, angleRad) {
                    const translated = {
                        x: point.x - center.x,
                        y: point.y - center.y,
                    };

                    const rotated = {
                        x: translated.x * Math.cos(angleRad) - translated.y * Math.sin(angleRad),
                        y: translated.x * Math.sin(angleRad) + translated.y * Math.cos(angleRad),
                    };

                    return {
                        x: center.x + rotated.x,
                        y: center.y + rotated.y,
                    };
                }

                const CommonProps = {
                    position: renderPosition,
                    lanterns,
                    colorList,
                    luminaires,
                    props: { ...props, positionAngle: renderAngle },
                    rotateAround,
                    barCenter,
                    centerOffset,
                    barThickness, // might need updating with new scale
                    lineRefs,
                    polarInfo: props.polarInfo,
                    updateLanterns: props.updateLanterns,
                    fullArmLength,
                    endPointSize,
                    beamEndPointSize,
                };


                return (
                    <>
                        {/* Lantern arms + heads */}
                        {props.values.configuration === "None" && <WallColumnNone {...CommonProps} />}
                        {props.values.configuration === "Beam Aiming" && <WallColumnBeam {...CommonProps} />}
                        {props.values.configuration === "Asymmetrical Flood Aiming" && <WallColumnAsym {...CommonProps} />}



                        {/* Bar centered at position */}
                        <Rect
                            x={barCenter.x}
                            y={barCenter.y}
                            width={width}
                            height={circleRadius}
                            offset={{ x: width / 2, y: circleRadius / 2 }}
                            fill="lightgray"
                            rotation={angle}
                            onClick={handleSelect}
                            onTap={handleSelect}
                            onMouseEnter={() => setHoverColumn(true)}
                            onMouseLeave={() => setHoverColumn(false)}
                        />

                        {/* Text above position.x/y — no rotation applied */}
                        {hoverColumn && (
                            <Text
                                ref={textRef}
                                x={renderPosition.x - textWidth / 2}
                                y={renderPosition.y - ((getDynamicFontSize() + 2.75) * 2) - 1}
                                text={props.name}
                                fontSize={getDynamicFontSize()}
                                fill="black"
                                fontStyle="bold"
                                padding={5}
                                backgroundColor="gray"
                                opacity={0.75}
                                listening={false}
                                {...textFlipProps}
                            />
                        )}

                        {/* Transformer anchor, transparent and draggable */}
                        <Rect
                            x={renderPosition.x}
                            y={renderPosition.y}
                            width={circleRadius}
                            height={circleRadius}
                            offset={{ x: circleRadius / 2, y: circleRadius / 2 }}
                            ref={shapeRef}
                            rotation={angle}
                            fill="transparent"
                            draggable={props.selected && !isLocked}
                            onDragMove={handleDragMove}
                            onDragEnd={(e) => props.updateColumn(e, props.i)}
                            onClick={handleSelect}
                            onTap={handleSelect}
                            onMouseEnter={() => setHoverColumn(true)}
                            onMouseLeave={() => setHoverColumn(false)}
                        />
                    </>
                );
            })()}



            {props.selected && (
                <Transformer
                    ref={trRef}
                    keepRatio={true}
                    enabledAnchors={false}
                    rotateAnchorOffset={anchorOffset}
                    rotateEnabled={props.values.configuration === "None" || !isMast}
                    onTransformEnd={(e) => {
                        const newRotation = e.currentTarget.attrs.rotation;
                        const normalizedRenderRotation = normalize360(newRotation || 0);
                        const stored = toStoredAngle(normalizedRenderRotation);
                        const normalizedStored = normalize360(stored);
                        const applied = normalizedStored;
                        if (shapeRef.current) {
                            shapeRef.current.rotation(applied);
                        }
                        if (trRef.current) {
                            trRef.current.rotation(applied);
                        }
                        // Persist in stored/UI angle space so rerender doesn't flip orientation.
                        props.onRotate?.(normalizedStored, props.i);
                    }}
                />
            )}
        </>
    );
}
